import axios from 'axios';
import { API_URL } from './config';

const SESSION_KEY = 'bolfit.session';
const ADMIN_KEY = 'bolfit.adminKey';

// Session persistence: storage can be unavailable (private mode), so failures are ignored
export const loadSession = () => {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
};

export const saveSession = (session) => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {}
};

export const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {}
};

// The admin key of the panel is kept on the device so the stage does not retype it
export const loadAdminKey = () => {
  try {
    return localStorage.getItem(ADMIN_KEY) || '';
  } catch {
    return '';
  }
};

export const saveAdminKey = (key) => {
  try {
    localStorage.setItem(ADMIN_KEY, key);
  } catch {}
};

export const clearAdminKey = () => {
  try {
    localStorage.removeItem(ADMIN_KEY);
  } catch {}
};

const authHeaders = (sessionToken) => ({ Authorization: `Bearer ${sessionToken}` });

export const register = async (countryCode, phone, nickname) => {
  const { data } = await axios.post(`${API_URL}/register`, { countryCode, phone, nickname });
  return data;
};

export const fetchChatToken = async (sessionToken) => {
  const { data } = await axios.post(`${API_URL}/auth`, {}, { headers: authHeaders(sessionToken) });
  return data;
};

export const fetchHistory = async (sessionToken) => {
  const { data } = await axios.get(`${API_URL}/history`, { headers: authHeaders(sessionToken) });
  return data;
};

// The custom emotes of the chat: whatever the admin dropped in the emotes/ folder of the web bucket
export const fetchEmotes = async () => {
  const { data } = await axios.get(`${API_URL}/emotes`);
  return data.emotes;
};

// Admin panel: its state, and the draws that start the animation everywhere
export const fetchPanelState = async (adminKey) => {
  const { data } = await axios.get(`${API_URL}/admin/roulette`, { headers: authHeaders(adminKey) });
  return data;
};

// The clients' jackpot: a name among everyone who bought, drawn on every device at once
export const drawBuyer = async (adminKey) => {
  const { data } = await axios.post(`${API_URL}/admin/buyers`, {}, { headers: authHeaders(adminKey) });
  return data;
};

// The jackpot machine: a number between `from` and `to`, drawn on every device at once
export const launchJackpot = async (adminKey, { from, to }) => {
  const { data } = await axios.post(`${API_URL}/admin/jackpot`, { from, to }, { headers: authHeaders(adminKey) });
  return data;
};

// The scoreboard of the team games, changed on every device at once. `change` is one of
// { action: 'add', team, points }, { action: 'reset' }, { action: 'start', minutes } or { action: 'stop' }
export const updateScoreboard = async (adminKey, change) => {
  const { data } = await axios.post(`${API_URL}/admin/scoreboard`, change, { headers: authHeaders(adminKey) });
  return data;
};

// The stopwatch of the stage: `action` is 'start', 'pause' or 'reset'
export const updateStopwatch = async (adminKey, action) => {
  const { data } = await axios.post(`${API_URL}/admin/stopwatch`, { action }, { headers: authHeaders(adminKey) });
  return data;
};

// What the phones show besides the chat: `piece` is 'scoreboard', 'stopwatch' or 'games' (the Sorteos tab)
export const updateDisplay = async (adminKey, piece, visible) => {
  const { data } = await axios.post(`${API_URL}/admin/display`, { piece, visible }, { headers: authHeaders(adminKey) });
  return data;
};

// Moderation: a token for the panel to watch the room, delete messages and disconnect people
export const fetchModeratorToken = async (adminKey) => {
  const { data } = await axios.post(`${API_URL}/admin/chat-token`, {}, { headers: authHeaders(adminKey) });
  return data;
};

// level: 'muted' (cannot write, keeps watching and stays in the draws) or 'none' (can write again)
export const blockParticipant = async (adminKey, { participantId, nickname, level }) => {
  const { data } = await axios.post(
    `${API_URL}/admin/block`,
    { participantId, nickname, level },
    { headers: authHeaders(adminKey) }
  );
  return data;
};

// Review queue: what people wrote and is waiting for the admin
export const fetchPending = async (adminKey) => {
  const { data } = await axios.get(`${API_URL}/admin/pending`, { headers: authHeaders(adminKey) });
  return data.messages;
};

export const resolvePending = async (adminKey, { action, ids }) => {
  const { data } = await axios.post(
    `${API_URL}/admin/pending`,
    { action, ids },
    { headers: authHeaders(adminKey) }
  );
  return data;
};

// How long a message waits for the panel before it goes out on its own
export const setReviewWindow = async (adminKey, reviewWindowSeconds) => {
  const { data } = await axios.post(
    `${API_URL}/admin/config`,
    { reviewWindowSeconds },
    { headers: authHeaders(adminKey) }
  );
  return data;
};

// What the room already shows, for the panel when it opens
export const fetchModeratorHistory = async (adminKey) => {
  const { data } = await axios.get(`${API_URL}/admin/history`, { headers: authHeaders(adminKey) });
  return data.messages;
};

// Out of the room and out of the history, so it does not come back on a reload (messages only)
export const deleteMessage = async (adminKey, id) => {
  const { data } = await axios.post(`${API_URL}/admin/delete`, { id }, { headers: authHeaders(adminKey) });
  return data;
};

// A purchase is never removed, only its photo: the card stays, waiting or published
export const removePhoto = async (adminKey, id) => {
  const { data } = await axios.post(`${API_URL}/admin/photo`, { id }, { headers: authHeaders(adminKey) });
  return data;
};

// Admin report: the only place with phone, name and nickname together
export const fetchUsers = async (adminKey) => {
  const { data } = await axios.get(`${API_URL}/admin/users`, { headers: authHeaders(adminKey) });
  return data.users;
};

// The clean-ups between events: `scope` is 'users' (everyone, purchases included), 'buyers'
// (the purchases only) or 'chat'. The backend works a while per call and says whether anything
// is left, so this calls again until it is done. There is no undoing any of them
const CLEAR_ROUNDS = 10;
export const clearData = async (adminKey, scope) => {
  let data;
  for (let round = 0; round < CLEAR_ROUNDS && !data?.done; round++) {
    ({ data } = await axios.post(`${API_URL}/admin/clear`, { scope }, { headers: authHeaders(adminKey) }));
  }
  return data;
};

export const isUnauthorized = (err) => err?.response?.status === 401;

export const errorMessage = (err) =>
  err?.response?.data?.error || 'No pudimos conectarnos, revisá tu conexión e intentá de nuevo';
